import { OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, EventEmitter, HostListener, Input, Output, } from '@angular/core';
import { Subject } from 'rxjs';
import { TooltipComponent } from './tooltip.component';
import * as i0 from "@angular/core";
import * as i1 from "@angular/cdk/overlay";
let nextId = 0;
export class TooltipDirective {
    elementRef;
    overlay;
    renderer;
    destroy$ = new Subject();
    tooltipWindowId = `mhe-tooltip-${nextId++}`;
    overlayRef;
    tooltipRef;
    boundKeyDownFunc = this.onKeyDown.bind(this);
    tooltipMessage;
    isTap = false;
    observer;
    mheTooltip = '';
    placement = 'top';
    disableDefaultActions = false;
    disableTooltip = false;
    panelClass = '';
    alternateAriaLabel = '';
    shown = new EventEmitter();
    hidden = new EventEmitter();
    toggleShown = new EventEmitter();
    onTouch() {
        this.isTap = true;
    }
    onTouchEnd() {
        this.isTap = false;
    }
    onFocus() {
        this.toggle();
    }
    onBlur() {
        this.close();
    }
    onMouseover(target) {
        if (target && target.disabled) {
            return;
        }
        this.open();
    }
    onmouseout() {
        this.close();
    }
    constructor(elementRef, overlay, renderer) {
        this.elementRef = elementRef;
        this.overlay = overlay;
        this.renderer = renderer;
        this.observer = this.createDisabledMutationObserver();
    }
    ngOnChanges(changes) {
        this.onPlacementChange(changes.placement);
        this.onMessageChange(changes.mheTooltip);
        this.setAriaLabel();
    }
    ngOnDestroy() {
        this.close();
        this.observer.disconnect();
        this.destroy$.next();
        this.destroy$.complete();
    }
    close() {
        if (this.tooltipRef) {
            this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
            this.tooltipRef.destroy();
            this.overlayRef.dispose();
            this.tooltipRef = null;
            window.removeEventListener('keydown', this.boundKeyDownFunc);
            this.hidden.emit('TooltipDirective');
        }
    }
    isOpen() {
        return this.tooltipRef != null;
    }
    open() {
        if (this.isOpen() || this.disableTooltip || this.mheTooltip.length === 0 || this.isTap) {
            return;
        }
        this.createTooltip();
        window.addEventListener('keydown', this.boundKeyDownFunc);
        this.shown.emit('TooltipDirective');
        this.toggleShown.emit('TooltipDirective');
    }
    toggle() {
        if (this.tooltipRef) {
            this.close();
        }
        else {
            this.open();
        }
    }
    createTooltip() {
        const overlayConfig = this.getOverlayConfig();
        this.overlayRef = this.overlay.create(overlayConfig);
        const containerPortal = new ComponentPortal(TooltipComponent);
        const componentRef = this.overlayRef.attach(containerPortal);
        const hostEl = this.elementRef;
        const tooltipEl = componentRef.instance.el;
        componentRef.instance.id = this.tooltipWindowId;
        componentRef.instance.placement = this.placement;
        componentRef.instance.close = () => {
            this.close();
        };
        componentRef.instance.afterViewInit.subscribe(() => {
            this.updatePositionStrategy(this.placement, hostEl, tooltipEl);
        });
        this.setTooltipMessage(componentRef, this.mheTooltip);
        this.updatePositionStrategy(this.placement, hostEl, tooltipEl);
        this.tooltipRef = componentRef;
    }
    getOverlayConfig() {
        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            panelClass: this.panelClass,
        });
        return overlayConfig;
    }
    updatePositionStrategy(placement, hostEl, tooltipEl) {
        const position = this.getPosition(placement, hostEl, tooltipEl);
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([position]);
        this.overlayRef.updatePositionStrategy(positionStrategy);
    }
    getPosition(placement, hostEl, tooltipEl) {
        if (placement === 'top') {
            return {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -10,
            };
        }
        else if (placement === 'bottom') {
            return {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
            };
        }
        else if (placement === 'left') {
            const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
            const tooltipHeight = Number(tooltipEl.nativeElement.scrollHeight);
            return {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'bottom',
                offsetX: -10,
                offsetY: Math.round((hostElHeight + tooltipHeight) / 2),
            };
        }
        else if (placement === 'right') {
            const hostElHeight = Number(hostEl.nativeElement.scrollHeight);
            const tooltipHeight = Number(tooltipEl.nativeElement.scrollHeight);
            return {
                originX: 'end',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
                offsetY: Math.round((hostElHeight + tooltipHeight) / 2),
            };
        }
        else {
            // Top is default
            return {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -10,
            };
        }
    }
    onPlacementChange(change) {
        if (this.tooltipRef && change) {
            this.tooltipRef.instance.placement = change.currentValue;
        }
    }
    onMessageChange(change) {
        if (this.tooltipRef && change) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.setTooltipMessage(this.tooltipRef, change.currentValue);
        }
    }
    setTooltipMessage(componentRef, message) {
        if (!componentRef) {
            return;
        }
        componentRef.instance.tooltipMessage = this.mheTooltip;
        this.tooltipMessage = this.mheTooltip;
    }
    /**
     * This is a workaround for FF to close tooltip when host button is programmatically disabled
     *  because `blur` event is not fired when element is disabled
     * Read more here: https://bugzilla.mozilla.org/show_bug.cgi?id=559561
     */
    createDisabledMutationObserver() {
        const observer = new MutationObserver((list) => {
            for (const record of list) {
                const target = record?.target;
                if (target?.disabled !== undefined) {
                    const disabled = Boolean(target?.disabled);
                    this.onDisabledChange(disabled);
                }
            }
        });
        observer.observe(this.elementRef.nativeElement, { attributeFilter: ['disabled'] });
        return observer;
    }
    onDisabledChange(disabled) {
        if (disabled) {
            this.close();
        }
    }
    onKeyDown(event) {
        if (!this.disableDefaultActions && event.key === 'Escape') {
            this.close();
        }
    }
    setAriaLabel() {
        let element;
        if (this.elementRef.nativeElement.tagName.toLowerCase() === 'mhe-button') {
            element = this.elementRef.nativeElement.firstElementChild;
        }
        else {
            element = this.elementRef.nativeElement;
        }
        this.renderer.setAttribute(element, 'aria-label', this.alternateAriaLabel || this.mheTooltip);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ElementRef }, { token: i1.Overlay }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: TooltipDirective, selector: "[mheTooltip]", inputs: { mheTooltip: "mheTooltip", placement: "placement", disableDefaultActions: "disableDefaultActions", disableTooltip: "disableTooltip", panelClass: "panelClass", alternateAriaLabel: "alternateAriaLabel" }, outputs: { shown: "shown", hidden: "hidden", toggleShown: "toggleShown" }, host: { listeners: { "touchstart": "onTouch()", "touchend": "onTouchEnd()", "touchcancel": "onTouchEnd()", "focusin": "onFocus()", "focusout": "onBlur()", "mouseenter": "onMouseover($event.target)", "mouseleave": "onmouseout()" } }, exportAs: ["mheTooltip"], usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mheTooltip]',
                    exportAs: 'mheTooltip',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.Overlay }, { type: i0.Renderer2 }]; }, propDecorators: { mheTooltip: [{
                type: Input
            }], placement: [{
                type: Input
            }], disableDefaultActions: [{
                type: Input
            }], disableTooltip: [{
                type: Input
            }], panelClass: [{
                type: Input
            }], alternateAriaLabel: [{
                type: Input
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], toggleShown: [{
                type: Output
            }], onTouch: [{
                type: HostListener,
                args: ['touchstart']
            }], onTouchEnd: [{
                type: HostListener,
                args: ['touchend']
            }, {
                type: HostListener,
                args: ['touchcancel']
            }], onFocus: [{
                type: HostListener,
                args: ['focusin']
            }], onBlur: [{
                type: HostListener,
                args: ['focusout']
            }], onMouseover: [{
                type: HostListener,
                args: ['mouseenter', ['$event.target']]
            }], onmouseout: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });
